import moment from "moment";
import { NON_SORTABLE_COLUMNS, STATUS_BADGE_CLASSES } from "./constants";
import {
  ColumnConfig,
  FilterType,
  Option,
  Row,
  TabConfig,
  WorklistFilter,
} from "./types";

/**
 * Case-insensitive property read. ASP.NET camel-cases object properties
 * ("tabID", "tabHeaders") but NOT dictionary keys (row aliases stay "RequisitionStatus"),
 * and some deployments disable camel-casing entirely. This works for all of them.
 */
export function pick<T = unknown>(
  obj: unknown,
  ...names: string[]
): T | undefined {
  if (!obj || typeof obj !== "object") return undefined;
  const o = obj as Record<string, unknown>;
  for (const n of names) if (n in o) return o[n] as T;
  const lower = new Map(Object.keys(o).map((k) => [k.toLowerCase(), k]));
  for (const n of names) {
    const k = lower.get(n.toLowerCase());
    if (k !== undefined) return o[k] as T;
  }
  return undefined;
}

// RequisitionOrderId of a row (column "__RowKey" added by sp_ViewRequisitions_v2).
export const rowKey = (row: Row): number =>
  Number(pick(row, "__RowKey", "RequisitionOrderId") ?? 0);

export const rowText = (row: Row | null | undefined, ...names: string[]) => {
  const v = pick(row, ...names);
  return v === null || v === undefined ? "" : String(v);
};

// "<expr> as Alias" -> "Alias"
const aliasOf = (columnName?: string) => {
  if (!columnName) return undefined;
  const m = /\sas\s+\[?([A-Za-z0-9_]+)\]?\s*$/i.exec(columnName);
  return m?.[1];
};

// Value of a configured column in a row: tries ColumnKey, ColumnValue, then the SQL alias.
export function cellValue(row: Row, col: ColumnConfig): unknown {
  return pick(
    row,
    ...([col.key, col.columnValue, aliasOf(col.columnName)].filter(
      Boolean
    ) as string[])
  );
}

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}(T00:00:00(\.0+)?Z?)?$/;
const ISO_DATE_TIME =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;
const TIME_ONLY = /^\d{2}:\d{2}(:\d{2})?$/;

// Display value: ISO dates → MM/DD/YYYY, date-times → MM/DD/YYYY hh:mm A, times → hh:mm A.
export function formatCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.map(formatCell).join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  if (ISO_DATE_ONLY.test(s)) return moment(s.slice(0, 10)).format("MM/DD/YYYY");
  if (ISO_DATE_TIME.test(s)) return moment(s).format("MM/DD/YYYY hh:mm A");
  if (TIME_ONLY.test(s)) return moment(s, "HH:mm:ss").format("hh:mm A");
  return s;
}

const FILTER_TYPES: FilterType[] = [
  "text",
  "prefix",
  "exact",
  "dropdown",
  "multiselect",
  "daterange",
  "datepicker",
  "timerange",
];
const toFilterType = (t: unknown): FilterType | undefined => {
  const v = String(t ?? "")
    .trim()
    .toLowerCase() as FilterType;
  return FILTER_TYPES.includes(v) ? v : undefined;
};

// Normalise the TabColumnsV2 payload (ViewReqTabsWithHeadersResponse[]) into TabConfig[].
export function normalizeTabs(data: unknown): TabConfig[] {
  const list = Array.isArray(data) ? data : [];
  return list
    .map((t) => ({
      tabId: Number(pick(t, "tabID", "tabId")),
      tabName: String(pick(t, "tabName") ?? ""),
      sortOrder: Number(pick(t, "sortOrder") ?? 0),
      columns: ((pick<unknown[]>(t, "tabHeaders") ?? []) as unknown[]).map(
        (c) => ({
          id: Number(pick(c, "id")),
          label: String(pick(c, "columnLabel") ?? ""),
          key: String(pick(c, "columnKey") ?? ""),
          columnName: pick<string>(c, "columnName") ?? undefined,
          columnValue: pick<string>(c, "columnValue") ?? undefined,
          filterColumns: pick<string>(c, "filterColumns") ?? undefined,
          filterColumnsType: pick<string>(c, "filterColumnsType") ?? undefined,
          filterType: toFilterType(pick(c, "filterColumnsType")),
          fieldName: pick<string>(c, "fieldName") ?? undefined,
          isShow: Boolean(pick(c, "isShow")),
          isShowOnUi: pick(c, "isShowOnUi") !== false,
          isExpand: Boolean(pick(c, "isExpandData")),
          isEditable: Boolean(pick(c, "isIndividualEditable")),
          optionsJson: pick<string>(c, "jsonOptionData") ?? null,
        })
      ),
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Parse JsonOptionData. Accepts the common shapes:
 * [{label,value}], [{Label,Value}], [{text,value}], [{name,id}], ["A","B"].
 */
export function parseOptions(json: unknown): Option[] {
  let data: unknown = json;
  if (typeof json === "string") {
    if (!json.trim()) return [];
    try {
      data = JSON.parse(json);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(data)) return [];
  return data
    .map((o) => {
      if (o === null || o === undefined) return null;
      if (typeof o !== "object") return { label: String(o), value: String(o) };
      const value = pick(o, "value", "id", "key");
      const label = pick(o, "label", "text", "name", "displayName") ?? value;
      return value === undefined
        ? null
        : { label: String(label), value: String(value) };
    })
    .filter((o): o is Option => !!o);
}

export const isoDaysAgo = (days: number) =>
  moment().subtract(days, "days").format("YYYY-MM-DD");

export type FilterValue = Pick<WorklistFilter, "value" | "values" | "from" | "to">;

// Full request filter for a column: id + value, plus the legacy v1 fields.
export function toRequestFilter(
  col: ColumnConfig,
  v: FilterValue
): WorklistFilter {
  // Ranges travel in from/to only (no guessed legacy format).
  const filterValue = v.values?.join(",") ?? v.value;
  return {
    id: col.id,
    ...v,
    columnName: col.filterColumns ?? col.columnName ?? col.key,
    columnType: col.filterColumnsType ?? col.filterType,
    filterValue,
  };
}

export const defaultDateFilter = (
  dateCol: ColumnConfig | undefined,
  days: number
): WorklistFilter[] =>
  dateCol && days > 0
    ? [
        toRequestFilter(dateCol, {
          from: isoDaysAgo(days - 1),
          to: isoDaysAgo(0),
        }),
      ]
    : [];

export const fmtCount = (n?: number, capped?: boolean) =>
  n === undefined || n === null
    ? "…"
    : `${n.toLocaleString("en-US")}${capped ? "+" : ""}`;

export const compactCount = (n?: number) => {
  if (n === undefined || n === null || Number.isNaN(n)) return undefined;
  if (n >= 1e6)
    return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1).replace(/\.0$/, "")}M`;
  return n.toLocaleString("en-US");
};

export const isAbort = (e: unknown) => {
  const name = (e as { name?: string })?.name;
  const code = (e as { code?: string })?.code;
  return (
    name === "AbortError" || name === "CanceledError" || code === "ERR_CANCELED"
  );
};

// ------------------------------------------------------------ portal helpers
export const storage = {
  get<T>(key: string, fallback: T): T {
    try {
      const v = localStorage.getItem(key);
      return v ? (JSON.parse(v) as T) : fallback;
    } catch {
      return fallback;
    }
  },
  set(key: string, value: unknown) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // storage unavailable (private mode / blocked) — preference just isn't remembered
    }
  },
};

export const isSortable = (col: ColumnConfig) =>
  !!col.key && !NON_SORTABLE_COLUMNS.includes(col.key.toLowerCase());

export const statusBadgeClass = (label: string) =>
  STATUS_BADGE_CLASSES[label.trim().toLowerCase()] ?? "badge-status-default";

export const humanizeKey = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

const fmtDate = (d?: string) =>
  d ? (moment(d, "YYYY-MM-DD", true).isValid() ? moment(d).format("MM/DD/YYYY") : d) : "…";

export function describeFilter(
  col: ColumnConfig,
  f: WorklistFilter,
  options: Option[]
) {
  if (f.values?.length)
    return f.values
      .map((v) => options.find((o) => o.value === v)?.label ?? v)
      .join(", ");
  if (f.value) {
    if (col.filterType === "dropdown")
      return options.find((o) => o.value === f.value)?.label ?? f.value;
    if (col.filterType === "text") return `contains "${f.value}"`;
    if (col.filterType === "prefix") return `starts with "${f.value}"`;
    return f.value;
  }
  return `${fmtDate(f.from)} → ${fmtDate(f.to)}`;
}

const csvCell = (v: string) =>
  /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;

export function buildCsv(rows: Row[], columns: ColumnConfig[]) {
  const header = columns.map((c) => csvCell(c.label)).join(",");
  const lines = rows.map((r) =>
    columns.map((c) => csvCell(formatCell(cellValue(r, c)))).join(",")
  );
  return [header, ...lines].join("\n");
}

export const exportFileName = (tabName?: string) =>
  `requisitions_${(tabName ?? "export").replace(/\s+/g, "-")}_${moment().format(
    "YYYY-MM-DD"
  )}.csv`;
